import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Container } from '../style';

import {
  FileSpreadsheet, FileText, Download, X, Lock,
  ChevronRight, BookOpen, LayoutGrid
} from 'lucide-react';

// ─── Animations ───────────────────────────────────────────────────────────────

const fadeIn = keyframes`from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); }`;
const overlayIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const popupIn = keyframes`from { opacity: 0; transform: scale(0.95) translateY(8px); } to { opacity: 1; transform: scale(1) translateY(0); }`;

// ─── Layout ───────────────────────────────────────────────────────────────────

const PageWrapper = styled.div`
  min-height: 100vh;
  padding: 40px 0 64px;
  background: #f7f8fa;
`;

const Hero = styled.div`
  background: #1a365d;
  border-radius: 14px;
  padding: 44px 52px;
  margin-bottom: 28px;
  animation: ${fadeIn} 0.45s ease both;
  @media (max-width: 768px) { padding: 28px 24px; }
`;

const HeroBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255,255,255,0.12);
  color: rgba(255,255,255,0.75);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  padding: 5px 12px;
  border-radius: 4px;
  margin-bottom: 18px;
`;

const HeroTitle = styled.h1`
  color: #fff;
  font-size: 28px;
  font-weight: 700;
  line-height: 1.3;
  margin: 0 0 10px;
`;

const HeroSub = styled.p`
  color: rgba(255,255,255,0.55);
  font-size: 14px;
  margin: 0;
  line-height: 1.6;
`;

// ─── Grid ─────────────────────────────────────────────────────────────────────

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

// ─── Card ─────────────────────────────────────────────────────────────────────

const Card = styled.div`
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
  overflow: hidden;
  animation: ${fadeIn} 0.45s ease both;
  animation-delay: ${p => p.$delay || '0s'};
`;

const CardHeader = styled.div`
  padding: 22px 26px 18px;
  border-bottom: 1px solid #eef1f6;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CardIconWrap = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 9px;
  background: ${p => p.$bg || '#ebf4ff'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${p => p.$color || '#2c5282'};
`;

const CardTitle = styled.h2`
  font-size: 15px;
  font-weight: 700;
  color: #1a365d;
  margin: 0;
`;

const CardBody = styled.div`
  padding: 20px 26px 24px;
`;

// ─── List ─────────────────────────────────────────────────────────────────────

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ListItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 14px;
  color: #2d3748;
  line-height: 1.6;
  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #2c5282;
    flex-shrink: 0;
    margin-top: 8px;
  }
`;

// ─── Doc row ──────────────────────────────────────────────────────────────────

const DocRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 18px;
  border: 1px solid #e2eaf4;
  border-radius: 10px;
  margin-bottom: 10px;
  transition: background 0.15s, border-color 0.15s;
  &:last-child { margin-bottom: 0; }
  &:hover { background: #f9fafb; border-color: #bee3f8; }
`;

const DocIconWrap = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: ${p => p.$bg || '#ebf4ff'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${p => p.$color || '#2c5282'};
`;

const DocInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const DocName = styled.p`
  font-size: 14px;
  font-weight: 600;
  color: #1a365d;
  margin: 0 0 2px;
`;

const DocMeta = styled.p`
  font-size: 12px;
  color: #718096;
  margin: 0;
`;

const DocActions = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
`;

const DownloadBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  border: 1px solid #e2eaf4;
  background: #f7f9fc;
  color: #2c5282;
  transition: background 0.15s, border-color 0.15s;
  &:hover { background: #ebf4ff; border-color: #bee3f8; }
`;

const DownloadBtnProtected = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid #e2eaf4;
  background: #f7f9fc;
  color: #2c5282;
  transition: background 0.15s, border-color 0.15s;
  &:hover { background: #ebf4ff; border-color: #bee3f8; }
`;

const LockBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #a0aec0;
  margin-left: 4px;
`;

// ─── Popup ────────────────────────────────────────────────────────────────────

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 25, 50, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: ${overlayIn} 0.2s ease;
  padding: 20px;
`;

const Popup = styled.div`
  background: #fff;
  border-radius: 14px;
  padding: 32px;
  width: 100%;
  max-width: 380px;
  animation: ${popupIn} 0.25s ease;
`;

const PopupHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const PopupIconWrap = styled.div`
  width: 46px;
  height: 46px;
  border-radius: 12px;
  background: #ebf4ff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2c5282;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #a0aec0;
  padding: 4px;
  display: flex;
  align-items: center;
  border-radius: 6px;
  transition: color 0.15s, background 0.15s;
  &:hover { color: #4a5568; background: #f7f8fa; }
`;

const PopupTitle = styled.h3`
  font-size: 17px;
  font-weight: 700;
  color: #1a365d;
  margin: 0 0 6px;
`;

const PopupSub = styled.p`
  font-size: 13px;
  color: #718096;
  margin: 0 0 20px;
  line-height: 1.55;
`;

const Label = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #4a5568;
  letter-spacing: 0.4px;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 11px 14px;
  border: 1.5px solid ${p => p.$error ? '#fc8181' : '#e2e8f0'};
  border-radius: 8px;
  font-size: 15px;
  color: #1a365d;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.15s;
  letter-spacing: 4px;
  &:focus { border-color: ${p => p.$error ? '#fc8181' : '#2c5282'}; }
  &::placeholder { letter-spacing: 0; color: #cbd5e0; }
`;

const ErrorMsg = styled.p`
  font-size: 12px;
  color: #e53e3e;
  margin: 8px 0 0;
`;

const PopupActions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
`;

const BtnPrimary = styled.button`
  flex: 1;
  padding: 11px;
  background: #2c5282;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  transition: background 0.15s;
  &:hover { background: #1a365d; }
`;

const BtnSecondary = styled.button`
  padding: 11px 18px;
  background: #f7f8fa;
  color: #4a5568;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
  &:hover { background: #edf2f7; }
`;

// ─── Component ────────────────────────────────────────────────────────────────

const MacroEco = () => {
  const { t } = useTranslation();
  const [activePopup, setActivePopup] = useState(null); // null | 1 | 2
  const [pinCode, setPinCode] = useState('');
  const [error, setError] = useState('');
  const correctPin1 = '2070';
  const correctPin2 = '2017';

  const microdoc = "https://abt.tj/files/micro2010.xlsx";
const statdoc = "https://abt.tj/files/micro2017.pdf";

  const ta = (key) => {
    const val = t(key, { returnObjects: true });
    return Array.isArray(val) ? val : [];
  };

  const features = ta('trainingPlan.sections.macroeconomics.content.features');
  const programs = ta('trainingPlan.sections.macroeconomics.content.programs');

  const handlePinSubmit = (e) => {
    e.preventDefault();
    const correctPin = activePopup === 1 ? correctPin1 : correctPin2;
    const fileUrl = activePopup === 1 ? microdoc : statdoc;
    if (pinCode === correctPin) {
      setError('');
      setActivePopup(null);
      setPinCode('');
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = '';
      link.click();
    } else {
      setError(t('trainingPlan.sections.documents.invalidPin'));
    }
  };

  const handleClose = () => {
    setActivePopup(null);
    setPinCode('');
    setError('');
  };

  return (
    <PageWrapper>
      <Container>
        <Hero>
          <HeroBadge>
            <BookOpen size={12} />
            {t('trainingPlan.badge', 'Учебный план')}
          </HeroBadge>
          <HeroTitle>{t('trainingPlan.sections.macroeconomics.title')}</HeroTitle>
          <HeroSub>{t('trainingPlan.sections.macroeconomics.content.intro')}</HeroSub>
        </Hero>

        <Grid>
          <Card $delay="0.05s">
            <CardHeader>
              <CardIconWrap><LayoutGrid size={18} /></CardIconWrap>
              <CardTitle>{t('trainingPlan.sections.macroeconomics.featuresTitle', 'Особенности')}</CardTitle>
            </CardHeader>
            <CardBody>
              <List>
                {features.map((f, i) => <ListItem key={i}>{f}</ListItem>)}
              </List>
            </CardBody>
          </Card>

          <Card $delay="0.1s">
            <CardHeader>
              <CardIconWrap><BookOpen size={18} /></CardIconWrap>
              <CardTitle>{t('trainingPlan.sections.macroeconomics.content.programsTitle')}</CardTitle>
            </CardHeader>
            <CardBody>
              <List>
                {programs.map((p, i) => <ListItem key={i}>{p}</ListItem>)}
              </List>
            </CardBody>
          </Card>
        </Grid>

        {/* Documents */}
        <Card style={{ marginTop: 20 }} $delay="0.15s">
          <CardHeader>
            <CardIconWrap><FileSpreadsheet size={18} /></CardIconWrap>
            <CardTitle>{t('trainingPlan.sections.documents.title')}</CardTitle>
          </CardHeader>
          <CardBody>

            {/* micro2010.xlsx — защищён PIN */}
            <DocRow>
              <DocIconWrap $bg="#e6fffa" $color="#276749">
                <FileSpreadsheet size={20} />
              </DocIconWrap>
              <DocInfo>
                <DocName>
                  {t('trainingPlan.sections.documents.file1.name', 'Макроэкономические показатели')}
                  <LockBadge><Lock size={10} /></LockBadge>
                </DocName>
                <DocMeta>XLSX · 2010–2024 · {t('trainingPlan.sections.documents.protected', 'Защищено паролем')}</DocMeta>
              </DocInfo>
              <DocActions>
                <DownloadBtnProtected onClick={() => setActivePopup(1)}>
                  <Download size={12} />
                  {t('trainingPlan.sections.documents.download', 'Скачать')}
                </DownloadBtnProtected>
              </DocActions>
            </DocRow>

            <DocRow>
              <DocIconWrap $bg="#fff5f5" $color="#c53030">
                <FileText size={20} />
              </DocIconWrap>
              <DocInfo>
                <DocName>
                  {t('trainingPlan.sections.documents.file2.name', 'Статистика банковского сектора')}
                  <LockBadge><Lock size={10} /></LockBadge>
                </DocName>
                <DocMeta>PDF · 2017–2025 · {t('trainingPlan.sections.documents.protected', 'Защищено паролем')}</DocMeta>
              </DocInfo>
              <DocActions>
                <DownloadBtnProtected onClick={() => setActivePopup(2)}>
                  <Download size={12} />
                  {t('trainingPlan.sections.documents.download', 'Скачать')}
                </DownloadBtnProtected>
              </DocActions>
            </DocRow>

          </CardBody>
        </Card>
      </Container>

      {/* PIN Popup */}
      {activePopup && (
        <Overlay onClick={handleClose}>
          <Popup onClick={e => e.stopPropagation()}>
            <PopupHeader>
              <PopupIconWrap><Lock size={20} /></PopupIconWrap>
              <CloseBtn onClick={handleClose}><X size={18} /></CloseBtn>
            </PopupHeader>
            <PopupTitle>{t('trainingPlan.sections.documents.popupTitle')}</PopupTitle>
            <PopupSub>{t('trainingPlan.sections.documents.pinLabel')}</PopupSub>
            <form onSubmit={handlePinSubmit}>
              <Label htmlFor="pinCode">{t('trainingPlan.sections.documents.pinPlaceholder')}</Label>
              <Input
                type="password"
                id="pinCode"
                value={pinCode}
                onChange={e => setPinCode(e.target.value)}
                placeholder="••••"
                autoFocus
                $error={!!error}
              />
              {error && <ErrorMsg>{error}</ErrorMsg>}
              <PopupActions>
                <BtnSecondary type="button" onClick={handleClose}>
                  {t('trainingPlan.sections.documents.cancelButton')}
                </BtnSecondary>
                <BtnPrimary type="submit">
                  <Download size={15} />
                  {t('trainingPlan.sections.documents.submitButton')}
                </BtnPrimary>
              </PopupActions>
            </form>
          </Popup>
        </Overlay>
      )}
    </PageWrapper>
  );
};

export default React.memo(MacroEco);